import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    AdventurePropertiesContract,
    type MapboxMap,
} from '@polaris-customerweb/sharedcomponents.core';
import { Adventure } from '@polaris-customerweb/sharedcomponents.map';
import {
    ConsoleLogService,
    LogService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { Point } from 'geojson';
import type { GeoJSONFeature } from 'mapbox-gl';
import { SetActiveAdventure } from '../components/adventure/adventure.actions';
import { AppState } from '../models/app-state';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';

@Injectable({
    providedIn: 'root',
})
export class AdventureMapService extends MapContentMapService {
    adventureSourceId = 'outfitters';
    layerSuffixSelected = 'Selected';
    layerSuffixUnselected = 'Unselected';
    sourceId = 'composite';
    sourceLayerIds: string[] = [];

    layerIds: string[] = [];
    layerIdsSelected: string[];
    layerIdsUnselected: string[];
    initialSelectedIds: string | string[] | undefined;

    private className = 'AdventureMapService';

    constructor(
        consoleLogService: ConsoleLogService,
        private logService: LogService,
        mapService: MapService,
        private store: Store<AppState>
    ) {
        super(consoleLogService, mapService);
    }

    addSources(mapboxMap: MapboxMap): void {
        const allLayersIds = this.mapService.getLayerIds(mapboxMap);
        this.layerIds = allLayersIds.filter((id) =>
            id.includes(this.adventureSourceId)
        );
        this.getLayerIds(this.layerIds);
        this.setSelectedFilter(mapboxMap, this.initialSelectedIds);
        this._layerIdsSelectable = [
            ...this.layerIdsUnselected,
            ...this.layerIdsSelected,
        ];
        this.initialSelectedIds = undefined;
        this.sourceLayerIds = this.sourceLayerIds = [
            ...new Set(
                this.layerIdsSelectable
                    .map(
                        (layer) =>
                            mapboxMap.getLayer(layer)?.['source-layer'] ?? ''
                    )
                    .filter((sourceLayerId) => Boolean(sourceLayerId))
            ),
        ];
        if (!this.sourceLayerIds) {
            this.logService.error(
                this.className,
                'Unable to find outfitter data sourceLayerId'
            );
        }
        this.store.dispatch(new SetActiveAdventure(undefined));
    }

    getLayerIds(layerIds: Readonly<string[]>): void {
        this.layerIdsSelected = layerIds.filter((id) =>
            id.includes(this.layerSuffixSelected)
        );
        this.layerIdsUnselected = layerIds.filter((id) =>
            id.includes(this.layerSuffixUnselected)
        );

        this.layerIds = [...this.layerIdsUnselected, ...this.layerIdsSelected];

        this.consoleLogService.debug(`Adventure layer IDs`, this.layerIds);
    }

    setLayerVisibility(mapboxMap: MapboxMap, visible: boolean): void {
        if (!this.layerIdsUnselected) {
            return;
        }
        if (visible) {
            this.layerIdsUnselected.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });
            this.layerIdsSelected.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });
        } else {
            this.layerIdsUnselected.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });
            this.layerIdsSelected.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });
        }
    }

    setSelectedFilter(
        mapboxMap: MapboxMap,
        selectedIds?: string | number | string[] | number[]
    ): void {
        if (!selectedIds) {
            this.store.dispatch(new SetActiveAdventure(undefined));
        }

        // adventure ids must be string, if they come in as number convert them
        const ids = Array.isArray(selectedIds)
            ? selectedIds.map((id: string | number) => id.toString())
            : selectedIds?.toString();

        if (!this.layerIds || this.layerIds.length === 0) {
            this.initialSelectedIds = ids;
            return;
        }
        this.mapService.applyLayerFilters(mapboxMap, this.layerIds, ids);
    }

    getMapAdventures(mapboxMap: MapboxMap) {
        let features: GeoJSONFeature[] = [];

        this.sourceLayerIds.forEach(
            (layerId) =>
                (features = [
                    ...features,
                    ...mapboxMap.querySourceFeatures(this.sourceId, {
                        sourceLayer: layerId,
                    }),
                ])
        );

        return this.layerIdsUnselected
            ? features
                  //filter out duplicates
                  .filter(
                      (value, _index, self) =>
                          self.find((feat) => feat.id === value.id) === value
                  )
                  .map(
                      (feature) =>
                          new Adventure({
                              geometry: feature.geometry as Point,
                              properties:
                                  feature.properties as AdventurePropertiesContract,
                              type: 'Feature',
                          })
                  )
            : [];
    }
}
